import React, { useEffect, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, Chip, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';

import Image from '~/components/Common/Image';

import type { MediaImage } from './OfferBuilderImagesVideo';

interface Props {
  mediaImages: Array<MediaImage>;
}

interface OrderedMediaImage {
  image: MediaImage;
  sortId: string;
}

function buildOrderedImages(images: Array<MediaImage>): Array<OrderedMediaImage> {
  return images.map((image) => ({
    image,
    sortId: image.publicId,
  }));
}

function mergeOrderedImages(
  currentImages: Array<OrderedMediaImage>,
  nextImages: Array<MediaImage>,
): Array<OrderedMediaImage> {
  const nextImagesById = new Map(nextImages.map((image) => [image.publicId, image]));
  const existingImages = currentImages
    .filter((orderedImage) => nextImagesById.has(orderedImage.sortId))
    .map((orderedImage) => ({
      ...orderedImage,
      image: nextImagesById.get(orderedImage.sortId) ?? orderedImage.image,
    }));
  const existingImageIds = new Set(existingImages.map((image) => image.sortId));
  const newImages = nextImages
    .filter((image) => !existingImageIds.has(image.publicId))
    .map((image) => ({
      image,
      sortId: image.publicId,
    }));

  return existingImages.concat(newImages);
}

export default function OfferBuilderMainImageLibrary({ mediaImages }: Props) {
  const [orderedImages, setOrderedImages] = useState<Array<OrderedMediaImage>>(() => buildOrderedImages(mediaImages));
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    setOrderedImages((currentImages) => mergeOrderedImages(currentImages, mediaImages));
  }, [mediaImages]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setOrderedImages((currentImages) => {
      const oldIndex = currentImages.findIndex((image) => image.sortId === active.id);
      const newIndex = currentImages.findIndex((image) => image.sortId === over.id);

      if (oldIndex < 0 || newIndex < 0) {
        return currentImages;
      }

      return arrayMove(currentImages, oldIndex, newIndex);
    });
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ px: 3, py: 2.5 }}>
        <Typography variant="h5">Main image library</Typography>
        <Chip
          label={`${mediaImages.length} ${mediaImages.length === 1 ? 'image' : 'images'}`}
          color="info"
          variant="outlined"
        />
      </Stack>
      <Divider />

      {orderedImages.length > 0 ? (
        <DndContext onDragEnd={handleDragEnd} sensors={sensors} collisionDetection={closestCenter}>
          <SortableContext items={orderedImages.map((image) => image.sortId)} strategy={rectSortingStrategy}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                  xl: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 2,
                p: 3,
              }}
            >
              {orderedImages.map((orderedImage) => (
                <ImageTile key={orderedImage.sortId} orderedImage={orderedImage} />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      ) : (
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No images have been added to this offer yet.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function ImageTile({ orderedImage }: { orderedImage: OrderedMediaImage }) {
  const { image, sortId } = orderedImage;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  const label = image.title || 'Offer image';

  return (
    <Box
      ref={setNodeRef}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        aspectRatio: '4 / 3',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.default',
        opacity: isDragging ? 0.72 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : 'auto',
        '&:hover .OfferBuilderMainImageLibrary-overlay, &:focus-within .OfferBuilderMainImageLibrary-overlay': {
          opacity: 1,
        },
        '&:hover .OfferBuilderMainImageLibrary-dragHandle, &:focus-within .OfferBuilderMainImageLibrary-dragHandle': {
          opacity: 1,
        },
      }}
    >
      <Image
        publicId={image.publicId}
        alt={label}
        options={{ width: 420, height: 315 }}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
      <Chip label={image.sourceLabel} size="small" sx={imageSourceChipSx} />
      <IconButton
        className="OfferBuilderMainImageLibrary-dragHandle"
        aria-label="Drag to reorder image"
        sx={imageDragHandleSx}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Stack
        className="OfferBuilderMainImageLibrary-overlay"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          left: 0,
          opacity: 0,
          transition: 'opacity 160ms ease-in-out',
          px: 1.5,
          py: 1.25,
          background: 'linear-gradient(180deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.62))',
        }}
      >
        <Typography
          variant="body1"
          color="common.white"
          fontWeight={500}
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            visibility: image.title ? 'visible' : 'hidden',
          }}
        >
          {image.title}
        </Typography>
        <Stack direction="row" spacing={0.75}>
          <IconButton aria-label="Edit image" sx={imageActionButtonSx}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton aria-label="Hide image" sx={imageActionButtonSx}>
            <VisibilityOffIcon fontSize="small" />
          </IconButton>
          <IconButton aria-label="Delete image" sx={imageActionButtonSx}>
            <DeleteIcon color="error" fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );
}

const imageActionButtonSx = {
  width: 34,
  height: 34,
  bgcolor: 'common.white',
  color: 'grey.800',
  '&:hover': {
    bgcolor: 'grey.100',
  },
};

const imageDragHandleSx = {
  position: 'absolute',
  top: 10,
  right: 10,
  width: 30,
  height: 34,
  borderRadius: 1.5,
  bgcolor: 'common.white',
  color: 'grey.800',
  cursor: 'grab',
  opacity: 0,
  boxShadow: 2,
  transition: 'opacity 160ms ease-in-out, background-color 160ms ease-in-out',
  '&:hover': {
    bgcolor: 'grey.100',
  },
  '&:active': {
    cursor: 'grabbing',
  },
};

const imageSourceChipSx = {
  position: 'absolute',
  top: 10,
  left: 10,
  maxWidth: 'calc(100% - 54px)',
  bgcolor: 'grey.800',
  color: 'common.white',
  fontWeight: 700,
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};
